/**
 * Imports
 */
import { createSelector } from '@reduxjs/toolkit';
import { CONTRACT_OPTIONS } from '../../../constants';
import { filter } from 'lodash';
import { VibrantlyState } from 'store/types';
import type { Firebase } from 'types/firebase';
import { SelectOption } from 'utils/OptionUtils';

/** A `contracts/{slug}` document as this dashboard reads it — id is the slug. */
type ContractDoc = { id: string; name?: string; active?: boolean };

/**
 * Selectors
 */
export const rootSelector = (state: VibrantlyState) => state.merchants;

export const showAddOwnerModalSelector = createSelector(
  [rootSelector],
  (root) => root.modals.ownerModal,
);

export const merchantsSelector = createSelector(
  (state: VibrantlyState) => state.firestore,
  (firestore) => {
    return firestore.ordered?.merchants?.filter(
      (m: Firebase.Merchant) => !m?.instantMerchant,
    ) as Record<string, Firebase.Merchant>;
  },
);
export const instantsMerchantsSelector = createSelector(
  (state: VibrantlyState) => state.firestore,
  (firestore) =>
    firestore.ordered?.merchants?.filter(
      (m: Firebase.Merchant) => m?.instantMerchant,
    ),
);
export const merchantSelector = (id: string) =>
  createSelector([firestoreRootSelector], (firestore) => {
    return firestore.data[`merchant_${id}`] as Firebase.Merchant;
  });
export const accountSelector = (id: string) =>
  createSelector(
    [firestoreRootSelector],
    (firestore) => firestore.data[`account_${id}`] as Firebase.ApiAccount,
  );
export const maxNumberOfTerminalsSelector = (id: string) =>
  createSelector(
    [accountSelector(id)],
    (account) => account?.settings?.maxNumberOfTerminals,
  );
export const firestoreRootSelector = createSelector(
  (state: VibrantlyState) => state.firestore,
  (root) => root,
);
export const currentMerchantSelector = createSelector(
  [firestoreRootSelector],
  (firestore) => firestore.data.merchant as Firebase.Merchant,
);

export const merchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants) => merchants?.length,
);
export const completedMerchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants: Record<number, Firebase.Merchant>) => {
    return filter(
      merchants,
      (merchant) =>
        merchant.clearhausKYCdone != null &&
        merchant.readyForApplication != null,
    ).length;
  },
);
export const pendingVibrantMerchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants) => {
    return filter(merchants, (merchant) => merchant.readyForApplication == null)
      .length;
  },
);
export const pendingClearhausMerchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants) => {
    return filter(merchants, (merchant) => merchant.clearhausKYCdone == null)
      ?.length;
  },
);
export const devicesSelector = createSelector(
  (state: VibrantlyState) => state.firestore,
  (firestore) => firestore?.data?.devices as Record<string, Firebase.Device>,
);
/**
 * Firestore
 */
export const partnerNameSelector = createSelector(
  [firestoreRootSelector],
  (root) => root.data?.partnerAccount?.name,
);
export const partnersSelector = createSelector(
  [firestoreRootSelector],
  (root) => root.data?.partners as Record<string, Firebase.Partner>,
);
export const partnerSelector = (partnerId?: string) =>
  createSelector([partnersSelector], (partners) => {
    if (partnerId) {
      return partners[partnerId];
    } else {
      return null;
    }
  });

/**
 * Contracts
 *
 * The `contracts` collection is managed on the v2 dashboard's /partners page (DEV-2664).
 * The document id is the slug — the value stored on `merchant.merchantContract` — so the
 * option value comes from `id`, never from a field.
 */
export const contractsSelector = createSelector(
  [firestoreRootSelector],
  (root) => root.ordered?.contracts as ContractDoc[] | undefined,
);

/**
 * The Merchant Contract dropdown.
 *
 * Falls back to the hardcoded CONTRACT_OPTIONS while the collection is empty or still
 * loading, so this dashboard behaves exactly as before in any environment where nobody has
 * imported the built-in list yet. Once the collection has an entry it is authoritative,
 * which is what makes "set inactive" on the v2 page take effect here too.
 *
 * `active !== false` rather than `active === true`: a document written without the field
 * counts as active, matching how the v2 dashboard parses it.
 */
export const contractOptionsSelector = createSelector(
  [contractsSelector],
  (contracts): SelectOption[] => {
    if (!contracts?.length) {
      return CONTRACT_OPTIONS;
    }
    return contracts
      .filter((c) => c?.id && c.active !== false)
      .map((c) => ({
        label: c.name?.trim() ? `${c.id} — ${c.name.trim()}` : c.id,
        value: c.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  },
);

/**
 * Filters
 */
export const filtersSelector = createSelector(
  [rootSelector],
  (root) => root.filters,
);

/**
 * terminals
 */
export const terminalListSelector = (accountId: string) =>
  createSelector([firestoreRootSelector], (firestore) => {
    const terminals = firestore.data[`terminals_${accountId}`];
    return terminals;
  });
